import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { workflowApi, type TaskView } from '@/api/modules/workflow';
import { useAuth } from '@/auth/useAuth';

type TabKey = 'mine' | 'group';

export function InboxPage() {
  const { session } = useAuth();
  const [tab, setTab] = useState<TabKey>('mine');
  const [group] = useState('warranty-reviewers');

  const tasksQuery = useQuery({
    queryKey: ['workflow', 'tasks', tab, tab === 'mine' ? session?.subject : group],
    queryFn: () =>
      tab === 'mine'
        ? workflowApi.tasksForAssignee(session?.subject ?? '')
        : workflowApi.tasksForGroup(group),
    enabled: tab === 'group' || Boolean(session?.subject)
  });

  return (
    <Box>
      <Typography variant="h1" gutterBottom>
        Workflow inbox
      </Typography>
      <Tabs value={tab} onChange={(_, v: TabKey) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="My tasks" value="mine" />
        <Tab label="Candidate group" value="group" />
      </Tabs>

      {tasksQuery.isLoading && <Typography>Loading…</Typography>}
      {tasksQuery.isError && (
        <Typography color="error">Failed to load tasks.</Typography>
      )}
      {tasksQuery.data && tasksQuery.data.length === 0 && (
        <Typography color="text.secondary">Inbox is empty.</Typography>
      )}
      <Stack spacing={2}>
        {tasksQuery.data?.map((task: TaskView) => (
          <Card key={task.id} variant="outlined">
            <CardContent>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                justifyContent="space-between"
                alignItems={{ sm: 'center' }}
              >
                <Box>
                  <Typography variant="h3">{task.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {task.processDefinitionKey}
                  </Typography>
                </Box>
                <Chip
                  label={task.assignee ?? 'unassigned'}
                  color={task.assignee ? 'primary' : 'default'}
                  size="small"
                />
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>
    </Box>
  );
}
